<template>
  <div class="main-wrapper">
    <layoutheader></layoutheader>

    <!-- Guest booking: verify the phone number before the appointment is created -->
    <div class="otp-page">
      <div class="otp-card">
        <router-link class="otp-back" :to="`/patient-details/${doctorId}`">
          <i class="fa-solid fa-arrow-left-long" aria-hidden="true"></i> {{ $t("back") }}
        </router-link>

        <div v-if="booking" class="otp-booking">
          <span class="otp-booking__icon" aria-hidden="true"><i class="feather-calendar"></i></span>
          <div class="otp-booking__text">
            <strong>{{ $t("otp_booking_with") }} {{ booking.doctor }}</strong>
            <span>{{ booking.when }}<template v-if="booking.clinic"> · {{ booking.clinic }}</template></span>
          </div>
        </div>

        <div class="otp-badge" aria-hidden="true"><i class="fab fa-whatsapp"></i></div>
        <h3 class="otp-title">{{ $t("otp_title") }}</h3>
        <p class="otp-lead">
          {{ $t("otp_sent_whatsapp") }}
          <strong class="otp-nowrap">{{ maskedPhone }}</strong>
          <router-link class="otp-change" :to="`/phone-number/${doctorId}`">{{ $t("otp_change") }}</router-link>
        </p>
        <p v-if="maskedEmail" class="otp-sub">{{ $t("otp_also_email") }} <strong>{{ maskedEmail }}</strong></p>

        <form class="otp-form" autocomplete="off" @submit.prevent="submitOtp">
          <div class="otp-boxes" :class="{ 'is-error': showError, 'is-shaking': shaking }" @animationend="shaking = false">
            <input
              v-for="(digit, index) in digits"
              :key="index"
              :ref="el => (inputs[index] = el)"
              :value="digit"
              type="text"
              inputmode="numeric"
              :autocomplete="index === 0 ? 'one-time-code' : 'off'"
              maxlength="1"
              class="otp-box"
              :class="{ 'is-filled': digit }"
              :aria-label="$t('otp_digit', { n: index + 1 })"
              :disabled="loading"
              @input="onDigitInput(index, $event)"
              @keydown="onDigitKeydown(index, $event)"
              @paste.prevent="onPaste"
              @focus="$event.target.select()"
            />
          </div>

          <p v-if="showError" class="otp-error" role="alert">
            <i class="feather-alert-circle" aria-hidden="true"></i> {{ error }}
          </p>
          <p v-else-if="resent" class="otp-success" role="status">
            <i class="feather-check-circle" aria-hidden="true"></i> {{ $t("otp_resent") }}
          </p>

          <button type="submit" class="otp-submit" :disabled="otp.length < OTP_LENGTH || loading">
            <span v-if="loading" class="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
            {{ loading ? $t("otp_verifying") : $t("otp_verify") }}
          </button>
        </form>

        <div class="otp-resend">
          <span>{{ $t("otp_didnt_get") }}</span>
          <button v-if="countDown === 0" type="button" class="otp-resend__link" :disabled="resending" @click="resendCode">
            {{ $t("otp_resend") }}
          </button>
          <span v-else class="otp-resend__timer">{{ $t("otp_resend_in", { time: countDownLabel }) }}</span>
        </div>
        <p class="otp-note"><i class="feather-lock" aria-hidden="true"></i> {{ $t("otp_valid_for") }}</p>
      </div>
    </div>
  </div>
</template>
<script>
import axios from "axios";
import moment from "moment";

const OTP_LENGTH = 4;
const RESEND_SECONDS = 30;

export default {
  data() {
    return {
      OTP_LENGTH,
      digits: Array(OTP_LENGTH).fill(""),
      inputs: [],
      countDown: RESEND_SECONDS,
      timer: null,
      error: null,
      showError: false,
      shaking: false,
      resent: false,
      resending: false,
      loading: false,
      doctorName: "",
      doctorId: this.$store.getters.getAppointmentDraft.doctorid,
      email: this.$store.getters.getEmail,
      mobileNumber: this.$store.getters.getMobileNumber
    };
  },
  computed: {
    otp() {
      return this.digits.join("");
    },
    countDownLabel() {
      return `0:${String(this.countDown).padStart(2, "0")}`;
    },
    // "+91 •••••• 3210"; the store is empty after a refresh in a new tab.
    maskedPhone() {
      const phone = this.mobileNumber || "";
      if (phone.length < 8) return this.$t("otp_your_number");
      const country = phone.length > 10 ? phone.slice(0, phone.length - 10) : "";
      return `${country} •••••• ${phone.slice(-4)}`.trim();
    },
    // "a•••@example.com"
    maskedEmail() {
      const [name, domain] = (this.email || "").split("@");
      return name && domain ? `${name[0]}•••@${domain}` : "";
    },
    booking() {
      const draft = this.$store.getters.getAppointmentDraft;
      if (!draft.day || !this.doctorName) return null;
      const date = moment(draft.day, "YYYY-MM-DD");
      const time = draft.time ? moment(draft.time, ["HH:mm:ss", "HH:mm"]) : null;
      return {
        doctor: `Dr. ${this.doctorName}`,
        when: [date.isValid() ? date.format("ddd, D MMM") : "", time && time.isValid() ? time.format("h:mm A") : ""]
          .filter(Boolean).join(", "),
        clinic: draft.clinicname
      };
    }
  },
  methods: {
    startCountDown() {
      clearInterval(this.timer);
      this.countDown = RESEND_SECONDS;
      this.timer = setInterval(() => {
        this.countDown -= 1;
        if (this.countDown <= 0) clearInterval(this.timer);
      }, 1000);
    },
    focusBox(index) {
      const box = this.inputs[Math.max(0, Math.min(index, OTP_LENGTH - 1))];
      if (box) box.focus();
    },
    clearError() {
      this.showError = false;
      this.resent = false;
    },
    // Fill boxes from `index` with the digits in `text`; submit once all are filled.
    fillFrom(index, text) {
      const chars = text.replace(/\D/g, "").split("");
      if (!chars.length) return;
      chars.slice(0, OTP_LENGTH - index).forEach((char, offset) => {
        this.digits[index + offset] = char;
      });
      this.clearError();
      const next = this.digits.findIndex(d => !d);
      if (next === -1) {
        this.inputs[OTP_LENGTH - 1].blur();
        this.submitOtp();
      } else {
        this.focusBox(next);
      }
    },
    onDigitInput(index, event) {
      const value = event.target.value;
      if (!/\d/.test(value)) {
        // Letters etc. are not part of a code: put the box back as it was.
        event.target.value = this.digits[index];
        return;
      }
      // Phones autofill the whole code into the first box.
      this.fillFrom(index, value.length > 1 ? value : value.slice(-1));
    },
    onDigitKeydown(index, event) {
      if (event.key === "Backspace") {
        event.preventDefault();
        if (this.digits[index]) {
          this.digits[index] = "";
        } else if (index > 0) {
          this.digits[index - 1] = "";
          this.focusBox(index - 1);
        }
        this.clearError();
      } else if (event.key === "ArrowLeft") {
        event.preventDefault();
        this.focusBox(index - 1);
      } else if (event.key === "ArrowRight") {
        event.preventDefault();
        this.focusBox(index + 1);
      }
    },
    onPaste(event) {
      this.fillFrom(0, (event.clipboardData || window.clipboardData).getData("text"));
    },
    showFailure(message) {
      this.error = message;
      this.showError = true;
      this.shaking = true;
      this.digits = Array(OTP_LENGTH).fill("");
      this.$nextTick(() => this.focusBox(0));
    },
    async resendCode() {
      this.resending = true;
      this.clearError();
      try {
        await axios.post("otp", {
          mobileNumber: this.mobileNumber,
          email: this.email || null,
        });
        this.digits = Array(OTP_LENGTH).fill("");
        this.resent = true;
        this.startCountDown();
        this.focusBox(0);
      } catch (error) {
        console.error("Error sending otp:", error);
        this.error = this.$t("otp_error_resend");
        this.showError = true;
      } finally {
        this.resending = false;
      }
    },
    async submitOtp() {
      if (this.loading || this.otp.length < OTP_LENGTH) return;
      this.loading = true;
      this.clearError();
      try {
        // email is optional for guests; send null rather than leaving it out.
        await axios.post("otp/validator", {
          mobileNumber: this.mobileNumber,
          email: this.email || null,
          otpCode: this.otp,
        });
      } catch (error) {
        console.error("Error validating OTP:", error);
        this.loading = false;
        this.showFailure(error.response && error.response.status === 400
          ? this.$t("otp_error_wrong")
          : this.$t("otp_error_verify"));
        return;
      }
      try {
          const patientDetails = {
            patientid: 0,
            contactnumbers: this.$store.getters.getMobileNumber,
            email: this.$store.getters.getEmail !== undefined ? this.$store.getters.getEmail : '',
            firstname: this.$store.getters.getfirstName,
            lastname: this.$store.getters.getlastName !== undefined ? this.$store.getters.getlastName : '',
            gender: this.$store.getters.getGender,
            listyn: true,
            area: "",
            areacode: "",
            address: "",
            age: "",
            dateof_birth: null,
            medicalhistory: "",
            geolocation: null,
            parentid: null,
            idp_id: "",
          };


          const postResponse = await axios.post("/patients", patientDetails);
          this.$store.dispatch("updateAppointmentDraft", {
            patientid: postResponse.data.patientid,
            appointmentby: postResponse.data.patientid,
          });
          this.$router.push("/booking-confirm");
      } catch (error) {
        // The code was correct; saving the guest's details is what failed.
        console.error("Error saving patient details:", error);
        this.showError = true;
        this.loading = false;
        this.error = this.$t("otp_error_save");
      }
    },
    async loadDoctorName() {
      if (!this.doctorId) return;
      try {
        let details = sessionStorage.getItem(`doctorDetails${this.doctorId}`);
        details = details ? JSON.parse(details) : (await axios.get(`doctors/${this.doctorId}`)).data;
        this.doctorName = details.doctorname || "";
      } catch (error) {
        // The summary is a nicety; the page works without it.
        console.error("Error fetching doctor details:", error);
      }
    }
  },
  created() {
    this.startCountDown();
    this.loadDoctorName();
  },
  mounted() {
    this.focusBox(0);
  },
  beforeUnmount() {
    clearInterval(this.timer);
  }
};
</script>

<style scoped>
.otp-page {
  --otp-blue: #0e82fd;
  --otp-text: #1f2937;
  --otp-muted: #6b7280;
  --otp-border: #d9dde4;
  --otp-error: #d93025;
  --otp-success: #138a4b;
  min-height: 100vh;
  /* Clears the fixed site header, like the theme's .login-content-info. */
  padding: 160px 16px 64px;
  background: #f5f8fb;
}

@media (max-width: 991.98px) {
  .otp-page {
    padding-top: 110px;
  }
}

.otp-card {
  max-width: 440px;
  margin: 0 auto;
  padding: 28px 32px 32px;
  background: #fff;
  border: 1px solid #e7ebf0;
  border-radius: 16px;
  box-shadow: 0 12px 40px rgba(16, 24, 40, 0.08);
  text-align: center;
}

.otp-back {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  float: left;
  font-size: 14px;
  color: var(--otp-muted);
}

.otp-back:hover {
  color: var(--otp-blue);
}

.otp-booking {
  clear: both;
  display: flex;
  align-items: center;
  gap: 12px;
  margin: 36px 0 24px;
  padding: 12px 14px;
  text-align: left;
  background: #f2f7ff;
  border: 1px solid #dbe9ff;
  border-radius: 12px;
}

.otp-booking__icon {
  display: inline-flex;
  flex: 0 0 36px;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  border-radius: 50%;
  background: #fff;
  color: var(--otp-blue);
}

.otp-booking__text {
  display: flex;
  flex-direction: column;
  min-width: 0;
  font-size: 13.5px;
  color: var(--otp-muted);
}

.otp-booking__text strong {
  font-size: 14.5px;
  color: var(--otp-text);
}

.otp-badge {
  clear: both;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 64px;
  height: 64px;
  margin-top: 8px;
  border-radius: 50%;
  background: #e8f8ee;
  color: #25d366;
  font-size: 32px;
}

.otp-booking + .otp-badge {
  margin-top: 0;
}

.otp-title {
  margin: 16px 0 8px;
  font-size: 22px;
  font-weight: 700;
  color: var(--otp-text);
}

.otp-lead {
  margin: 0;
  font-size: 15px;
  line-height: 1.55;
  color: var(--otp-muted);
}

.otp-lead strong,
.otp-sub strong {
  color: var(--otp-text);
}

.otp-nowrap {
  white-space: nowrap;
}

.otp-change {
  margin-left: 6px;
  font-weight: 600;
  color: var(--otp-blue);
}

.otp-sub {
  margin: 6px 0 0;
  font-size: 13.5px;
  color: var(--otp-muted);
}

.otp-form {
  margin-top: 28px;
}

.otp-boxes {
  display: flex;
  justify-content: center;
  gap: 12px;
}

.otp-box {
  width: 56px;
  height: 60px;
  padding: 0;
  border: 1.5px solid var(--otp-border);
  border-radius: 12px;
  background: #fff;
  font-size: 24px;
  font-weight: 700;
  text-align: center;
  color: var(--otp-text);
  caret-color: var(--otp-blue);
  outline: none;
  transition: border-color 0.15s, box-shadow 0.15s;
}

.otp-box:focus {
  border-color: var(--otp-blue);
  box-shadow: 0 0 0 4px rgba(14, 130, 253, 0.15);
}

.otp-box.is-filled {
  border-color: #9cc7fb;
}

.otp-boxes.is-error .otp-box {
  border-color: var(--otp-error);
}

.otp-boxes.is-error .otp-box:focus {
  box-shadow: 0 0 0 4px rgba(217, 48, 37, 0.14);
}

.otp-boxes.is-shaking {
  animation: otp-shake 0.35s ease-in-out;
}

@keyframes otp-shake {
  20%, 60% { transform: translateX(-6px); }
  40%, 80% { transform: translateX(6px); }
}

@media (prefers-reduced-motion: reduce) {
  .otp-boxes.is-shaking {
    animation: none;
  }
}

.otp-error,
.otp-success {
  display: flex;
  align-items: flex-start;
  justify-content: center;
  gap: 6px;
  margin: 12px 0 0;
  font-size: 13.5px;
  line-height: 1.4;
}

.otp-error {
  color: var(--otp-error);
}

.otp-success {
  color: var(--otp-success);
}

.otp-error i,
.otp-success i {
  margin-top: 2px;
}

.otp-submit {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 50px;
  margin-top: 24px;
  border: 0;
  border-radius: 10px;
  background: var(--otp-blue);
  color: #fff;
  font-size: 16px;
  font-weight: 600;
  transition: background 0.15s;
}

.otp-submit:hover:not(:disabled) {
  background: #0b6fd8;
}

.otp-submit:disabled {
  background: #a9cdfa;
  cursor: not-allowed;
}

.otp-resend {
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  gap: 6px;
  margin-top: 20px;
  font-size: 14px;
  color: var(--otp-muted);
}

.otp-resend__link {
  padding: 0;
  border: 0;
  background: none;
  font-weight: 600;
  color: var(--otp-blue);
}

.otp-resend__link:hover:not(:disabled) {
  text-decoration: underline;
}

.otp-resend__timer {
  font-variant-numeric: tabular-nums;
  color: var(--otp-text);
}

.otp-note {
  margin: 14px 0 0;
  font-size: 12.5px;
  color: #9aa1ad;
}

@media (max-width: 575.98px) {
  .otp-page {
    padding-right: 12px;
    padding-bottom: 40px;
    padding-left: 12px;
  }

  .otp-card {
    padding: 20px 18px 24px;
  }

  .otp-box {
    width: 52px;
    height: 56px;
  }
}
</style>
